import { getFrequencyAbbreviationDateSpan, getFrequencyAbbreviationText, useAbbreviateFrequencies } from './abbreviated/useAbbreviateFrequencies';
import { EditFrequenciesFormModal } from './edit/EditFrequenciesFormModal';
import { AbbreviatedFrequencyChip } from './abbreviated/AbbreviatedFrequenciesTable';
import { useFullOrder } from '../orders/orders/FullOrderContext';
import {
	DisciplineOptions,
	FrequencyCadenceTypeEnum,
	FrequencyStatusEnum,
	FrequencyTypeEnum,
	getFrequenciesInEffectForOrderOptions,
	type FrequencyViewModel,
	type FullOrderViewModel,
	type RoutinePrnFrequencyViewModel
} from '@generated';
import { OrderItems } from '../orders/OrderItems';
import { SectionListRow } from '~/shared/section/section-list/SectionListRow';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { AbbreviatedFrequencyModel, FrequencyDetailsModel } from './abbreviated/types';

export function hasFrequencyOrders(fullOrder: FullOrderViewModel) {
	return (
		fullOrder.evaluations.length > 0 ||
		fullOrder.routine.length > 0 ||
		fullOrder.routinePrnFrequencies.length > 0 ||
		fullOrder.discontinuedFrequencies.length > 0 ||
		fullOrder.discontinuedRoutinePrnFrequencies.length > 0
	);
}

function orderedFrequencies(fullOrder: FullOrderViewModel): FrequencyDetailsModel[] {
	return [
		...fullOrder.evaluations.map(x => ({
			...x,
			frequencyTypeId: FrequencyTypeEnum.Evaluation,
			frequencyCadenceTypeId: FrequencyCadenceTypeEnum.SpecificDateAndTime,
			frequencyStatusId: FrequencyStatusEnum.Drafted
		})),
		...fullOrder.routine.map(x => ({ ...x, frequencyTypeId: FrequencyTypeEnum.Routine, frequencyStatusId: FrequencyStatusEnum.Drafted }))
	];
}

function AbbreviatedFrequencyRow(frequencyAbbreviation: AbbreviatedFrequencyModel) {
	return (
		<SectionListRow
			label={
				<div className="flex-1 flex-col gap-2">
					<div className="flex flex-row items-center justify-between gap-2">
						<AbbreviatedFrequencyChip {...frequencyAbbreviation} />
						<span className="font-bold text-gray-600">{getFrequencyAbbreviationText(frequencyAbbreviation)}</span>
						<span className="flex text-gray-600">{getFrequencyAbbreviationDateSpan(frequencyAbbreviation)}</span>
					</div>
				</div>
			}
		/>
	);
}

function PrnFrequencyRow(prnFrequency: RoutinePrnFrequencyViewModel) {
	return (
		<SectionListRow
			label={
				<div className="flex-1 flex-col gap-2">
					<div className="flex flex-row items-center justify-between gap-2">
						<DisciplineChip disciplineId={prnFrequency.disciplineId} useAbbreviation />
						<span className="font-bold text-gray-600">{prnFrequency.count}</span>
						<span className="flex text-gray-600">{prnFrequency.prnReason}</span>
					</div>
				</div>
			}
		/>
	);
}

interface SummaryTotals {
	disciplineCounts: Record<number, number>;
	prnDisciplineCounts: Record<number, number>;
}

function getSummaryTotals(fullOrder: FullOrderViewModel): SummaryTotals {
	return {
		disciplineCounts: Object.fromEntries(
			DisciplineOptions.map(x => {
				const addedCount = [...fullOrder.evaluations, ...fullOrder.routine].filter(y => y.disciplineId === x.id).length;
				const removedCount = fullOrder.discontinuedFrequencies.filter(
					y =>
						y.disciplineId === x.id &&
						(y.frequencyTypeId === FrequencyTypeEnum.Evaluation || y.frequencyTypeId === FrequencyTypeEnum.Routine)
				).length;
				return [x.id, addedCount - removedCount];
			})
		),
		prnDisciplineCounts: Object.fromEntries(
			DisciplineOptions.map(x => {
				const addedCount = fullOrder.routinePrnFrequencies.filter(y => y.disciplineId === x.id).length;
				const removedCount = fullOrder.discontinuedRoutinePrnFrequencies.filter(y => y.disciplineId === x.id).length;
				return [x.id, addedCount - removedCount];
			})
		)
	};
}

function hasSummaryTotals({ disciplineCounts, prnDisciplineCounts }: SummaryTotals) {
	return DisciplineOptions.some(x => disciplineCounts[x.id] !== 0 || prnDisciplineCounts[x.id] !== 0);
}

function formatSignedCount(count: number) {
	return count > 0 ? `+${count}` : `${count}`;
}

function SummaryLegend({ disciplineCounts, prnDisciplineCounts }: SummaryTotals) {
	const entries = DisciplineOptions.flatMap(x => {
		const visits = disciplineCounts[x.id];
		const prn = prnDisciplineCounts[x.id];
		if (visits === 0 && prn === 0) {
			return [];
		}
		return [
			<div key={x.id} className="flex flex-row items-center gap-1.5">
				<DisciplineChip disciplineId={x.id} useAbbreviation />
				{visits !== 0 && <span className="font-bold">{formatSignedCount(visits)} visits</span>}
				{prn !== 0 && <span className="font-bold">{formatSignedCount(prn)} PRN</span>}
			</div>
		];
	});
	return <div className="flex flex-row flex-wrap items-center gap-4 border-t border-gray-200 pt-3 text-sm text-gray-600">{entries}</div>;
}

function getFrequenciesToAbbreviate(fullOrder: FullOrderViewModel, inEffectAfterOrder: FrequencyViewModel[]): FrequencyDetailsModel[] {
	const orderFrequencyDisciplines = [
		...fullOrder.evaluations.map(x => x.disciplineId),
		...fullOrder.routine.map(x => x.disciplineId),
		...fullOrder.routinePrnFrequencies.map(x => x.disciplineId),
		...fullOrder.discontinuedFrequencies.map(x => x.disciplineId),
		...fullOrder.discontinuedRoutinePrnFrequencies.map(x => x.disciplineId)
	];
	return [...orderedFrequencies(fullOrder), ...inEffectAfterOrder.filter(x => orderFrequencyDisciplines.includes(x.disciplineId))];
}

export function FrequencyOrders() {
	const { fullOrder } = useFullOrder();
	const abbreviateFrequencies = useAbbreviateFrequencies();
	const { data: inEffectAfterOrder } = useSuspenseQuery(getFrequenciesInEffectForOrderOptions(fullOrder.id));

	const abbreviatedFrequencies = abbreviateFrequencies(
		getFrequenciesToAbbreviate(fullOrder, inEffectAfterOrder),
		fullOrder.certificationPeriod
	).map(x => <AbbreviatedFrequencyRow key={x.id} {...x} />);

	const summaryTotals = getSummaryTotals(fullOrder);
	const items = hasSummaryTotals(summaryTotals)
		? [...abbreviatedFrequencies, <SummaryLegend key="summary-legend" {...summaryTotals} />]
		: abbreviatedFrequencies;

	return (
		<OrderItems
			title="Frequencies"
			items={items}
			sectionButtonProps={{
				label: 'Edit Frequencies',
				useSuspense: true,
				actionType: 'edit',
				renderModal: closeModal => <EditFrequenciesFormModal fullOrder={fullOrder} closeModal={closeModal} />
			}}
		/>
	);
}
